import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { PageLayout } from '../../components/ui/PageLayout';
import {
  DependentView,
  DependentRelationship,
  InsuranceStatus,
  DependentStatsView,
  DependentListQuery,
  DependentDocumentView,
  DependentDocumentType,
  getDependents,
  getDependentStats,
  getDependentDocuments,
  getDependentDocumentUrl,
} from '../../api/General/dependents';
import UpdateInsuranceModal from './components/UpdateInsuranceModal';
import BulkUpdateInsuranceModal from './components/BulkUpdateInsuranceModal';
import { Card, CardContent } from '../../components/ui/Card';
import {
  Dialog, DialogContent, DialogHeader, DialogBody,
  DialogTitle, DialogDescription,
} from '../../components/ui/Dialog';
import Button from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import Pagination from '@/components/Pagination';

const relationshipLabels: Record<DependentRelationship, string> = {
  [DependentRelationship.Spouse]: "Spouse",
  [DependentRelationship.Child]: "Child",
  [DependentRelationship.Parent]: "Parent",
  [DependentRelationship.Sibling]: "Sibling",
  [DependentRelationship.Other]: "Other",
};

const insuranceStatusLabels: Record<InsuranceStatus, string> = {
  [InsuranceStatus.Pending]: "Pending",
  [InsuranceStatus.Active]: "Active",
  [InsuranceStatus.Inactive]: "Inactive",
};

const insuranceStatusStyles: Record<InsuranceStatus, string> = {
  [InsuranceStatus.Pending]: "bg-warning/10 text-warning",
  [InsuranceStatus.Active]: "bg-success/10 text-success",
  [InsuranceStatus.Inactive]: "bg-destructive/10 text-destructive",
};

const documentTypeLabels: Record<DependentDocumentType, string> = {
  [DependentDocumentType.BirthCertificate]: "BirthCertificate",
  [DependentDocumentType.Passport]: "Passport",
  [DependentDocumentType.InsuranceCard]: "InsuranceCard",
  [DependentDocumentType.MedicalCertificate]: "MedicalCertificate",
  [DependentDocumentType.Other]: "Other",
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DependentsDirectory() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [dependents, setDependents] = useState<DependentView[]>([]);
  const [stats, setStats] = useState<DependentStatsView | null>(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  // Filters
  const [search, setSearch] = useState("");
  const [relationship, setRelationship] = useState<string>("all");
  const [insuranceStatus, setInsuranceStatus] = useState<string>("all");
  const [ageFilter, setAgeFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState("fullName");
  const [sortDescending, setSortDescending] = useState(false);

  // Details
  const [selectedDependent, setSelectedDependent] = useState<DependentView | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Documents
  const [documents, setDocuments] = useState<DependentDocumentView[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);

  // Selection for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Insurance modals
  const [insuranceModalOpen, setInsuranceModalOpen] = useState(false);
  const [bulkInsuranceModalOpen, setBulkInsuranceModalOpen] = useState(false);

  const ageRanges = [
    { label: t("dependents.ageChildren"), min: 0, max: 17 },
    { label: t("dependents.ageAdults"), min: 18, max: 64 },
    { label: t("dependents.ageSeniors"), min: 65, max: 150 },
  ];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const query: DependentListQuery = {
        search: search || undefined,
        relationship: relationship !== "all" ? relationship as DependentRelationship : undefined,
        insuranceStatus: insuranceStatus !== "all" ? insuranceStatus as InsuranceStatus : undefined,
        sortBy,
        sortDescending,
        page,
        pageSize,
      };

      // Apply age filter
      if (ageFilter !== "all") {
        const range = ageRanges.find(r => r.label === ageFilter);
        if (range) {
          query.minAge = range.min;
          query.maxAge = range.max;
        }
      }

      const [listResult, statsResult] = await Promise.all([
        getDependents(query),
        getDependentStats(),
      ]);

      if (listResult.errors.length === 0) {
        setDependents(listResult.payload.items);
        setTotalCount(listResult.payload.totalCount);
        setTotalPages(listResult.payload.totalPages);
      }

      if (statsResult.errors.length === 0) {
        setStats(statsResult.payload);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [search, relationship, insuranceStatus, ageFilter, sortBy, sortDescending, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Clear selection when data changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [dependents]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(field);
      setSortDescending(false);
    }
    setPage(1);
  };

  const handleFilterChange = () => {
    setPage(1);
  };

  const loadDocuments = useCallback(async (dependentId: number) => {
    setDocsLoading(true);
    try {
      const result = await getDependentDocuments(dependentId);
      if (result.errors.length === 0) {
        setDocuments(result.payload);
      }
    } catch {
      // silent
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const handleViewDocument = useCallback(async (docId: number) => {
    try {
      const result = await getDependentDocumentUrl(docId);
      if (result.errors.length === 0 && result.payload) {
        window.open(result.payload, "_blank");
      }
    } catch {
      // silent
    }
  }, []);

  // Selection handlers
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === dependents.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(dependents.map(d => d.id)));
    }
  };

  const selectedDependents = dependents.filter(d => selectedIds.has(d.id));

  // Insurance update handlers
  const handleInsuranceUpdated = (updated: DependentView) => {
    setDependents(prev => prev.map(d => d.id === updated.id ? updated : d));
    setSelectedDependent(updated);
  };

  const handleBulkInsuranceUpdated = () => {
    setSelectedIds(new Set());
    loadData();
  };

  return (
    <PageLayout
      title={t("dependents.directoryTitle")}
      description={t("dependents.directorySubtitle")}
      icon={Users}
      accentColor="amber"
      maxWidth="1600px"
      headerActions={
        selectedIds.size > 0 ? (
          <Button
            variant="outline"
            onClick={() => setBulkInsuranceModalOpen(true)}
            iconName="Shield"
          >
            {t('dependents.insurance.bulkUpdate', { count: selectedIds.size })}
          </Button>
        ) : undefined
      }
    >

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dependents.total")}</p>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                </div>
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dependents.activeInsurance")}</p>
                  <p className="text-2xl font-bold text-success">{stats.activeInsurance}</p>
                </div>
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dependents.pending")}</p>
                  <p className="text-2xl font-bold text-warning">{stats.pendingInsurance}</p>
                </div>
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dependents.children")}</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.children}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dependents.spouses")}</p>
                  <p className="text-2xl font-bold text-pink-600">{stats.spouses}</p>
                </div>
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dependents.parents")}</p>
                  <p className="text-2xl font-bold text-indigo-600">{stats.parents}</p>
                </div>
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card variant="elevated" className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="relative">
                <svg className="w-4 h-4 absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder={t("dependents.searchPlaceholder")}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); handleFilterChange(); }}
                  className="ps-10 pe-4 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary w-80"
                  data-testid="directory-search"
                />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  value={relationship}
                  onChange={(e) => { setRelationship(e.target.value); handleFilterChange(); }}
                  className="border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="directory-relationship-filter"
                >
                  <option value="all">{t("dependents.allRelationships")}</option>
                  {Object.entries(relationshipLabels).map(([val, label]) => (
                    <option key={val} value={val}>{t(`dependents.rel${label}`)}</option>
                  ))}
                </select>
                <select
                  value={insuranceStatus}
                  onChange={(e) => { setInsuranceStatus(e.target.value); handleFilterChange(); }}
                  className="border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="directory-insurance-filter"
                >
                  <option value="all">{t("dependents.allInsuranceStatus")}</option>
                  {Object.entries(insuranceStatusLabels).map(([val, label]) => (
                    <option key={val} value={val}>{t(`dependents.insurance${label}`)}</option>
                  ))}
                </select>
                <select
                  value={ageFilter}
                  onChange={(e) => { setAgeFilter(e.target.value); handleFilterChange(); }}
                  className="border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="directory-age-filter"
                >
                  <option value="all">{t("dependents.allAges")}</option>
                  {ageRanges.map(range => (
                    <option key={range.label} value={range.label}>{range.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card variant="elevated" className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : dependents.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="text-lg font-medium text-foreground mb-2">{t("dependents.noDependentsFound")}</h3>
            <p className="text-muted-foreground">{t("dependents.noDependentsFoundDescription")}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === dependents.length && dependents.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-border text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                      onClick={() => handleSort("fullName")}
                    >
                      <div className="flex items-center gap-1">
                        {t("dependents.dependentName")}
                        {sortBy === "fullName" && (
                          <svg className={`w-3 h-3 ${sortDescending ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                      onClick={() => handleSort("employee")}
                    >
                      {t("dependents.employee")}
                    </th>
                    <th
                      className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                      onClick={() => handleSort("relationship")}
                    >
                      {t("dependents.relationship")}
                    </th>
                    <th
                      className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                      onClick={() => handleSort("age")}
                    >
                      {t("dependents.age")}
                    </th>
                    <th
                      className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                      onClick={() => handleSort("insuranceStatus")}
                    >
                      {t("dependents.insuranceStatus")}
                    </th>
                    <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t("dependents.provider")}
                    </th>
                    <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t("dependents.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {dependents.map((dep) => (
                    <tr key={dep.id} className={`hover:bg-muted ${selectedIds.has(dep.id) ? 'bg-primary/5' : ''}`}>
                      <td className="px-4 py-4 w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(dep.id)}
                          onChange={() => toggleSelect(dep.id)}
                          className="rounded border-border text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div className="text-sm font-medium text-foreground">{dep.fullName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/profile/${dep.employeeId}/dependents`)}
                          className="text-sm font-medium text-primary hover:text-primary"
                        >
                          {dep.employeeName || "\u2014"}
                        </button>
                        <div className="text-xs text-muted-foreground">{dep.department || "\u2014"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {t(`dependents.rel${relationshipLabels[dep.relationship]}`)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {dep.age} {t("dependents.years")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${insuranceStatusStyles[dep.insuranceStatus]}`}>
                          {t(`dependents.insurance${insuranceStatusLabels[dep.insuranceStatus]}`)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {dep.insuranceProvider || "\u2014"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="min-h-[44px] min-w-[44px]"
                          onClick={() => { setSelectedDependent(dep); setDetailsOpen(true); loadDocuments(dep.id); }}
                          data-testid={`view-dir-dependent-${dep.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 border-t border-border">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={(p) => setPage(p)}
                  variant="compact"
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Details Modal */}
      <Dialog open={detailsOpen && !!selectedDependent} onOpenChange={(isOpen) => { if (!isOpen) setDetailsOpen(false); }}>
        <DialogContent size="lg">
          {selectedDependent && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <DialogTitle>{selectedDependent.fullName}</DialogTitle>
                    <DialogDescription>
                      {t(`dependents.rel${relationshipLabels[selectedDependent.relationship]}`)} {"\u2014"} {selectedDependent.employeeName}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <DialogBody className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">{t("dependents.personalDetails")}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-xs text-muted-foreground">{t("dependents.dateOfBirth")}</p><p className="text-sm text-foreground">{new Date(selectedDependent.dateOfBirth).toLocaleDateString()}</p></div>
                    <div><p className="text-xs text-muted-foreground">{t("dependents.age")}</p><p className="text-sm text-foreground">{selectedDependent.age} {t("dependents.years")}</p></div>
                    <div><p className="text-xs text-muted-foreground">{t("dependents.gender")}</p><p className="text-sm text-foreground">{selectedDependent.gender || "\u2014"}</p></div>
                    <div><p className="text-xs text-muted-foreground">{t("dependents.nationality")}</p><p className="text-sm text-foreground">{selectedDependent.nationality || "\u2014"}</p></div>
                    <div><p className="text-xs text-muted-foreground">{t("dependents.nationalId")}</p><p className="text-sm text-foreground">{selectedDependent.nationalId || "\u2014"}</p></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-foreground">{t("dependents.insuranceDetails")}</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setInsuranceModalOpen(true)}
                    >
                      <svg className="w-3.5 h-3.5 me-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      {t('dependents.insurance.updateStatus')}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-xs text-muted-foreground">{t("dependents.insuranceStatus")}</p><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${insuranceStatusStyles[selectedDependent.insuranceStatus]}`}>{t(`dependents.insurance${insuranceStatusLabels[selectedDependent.insuranceStatus]}`)}</span></div>
                    <div><p className="text-xs text-muted-foreground">{t("dependents.provider")}</p><p className="text-sm text-foreground">{selectedDependent.insuranceProvider || "\u2014"}</p></div>
                    <div><p className="text-xs text-muted-foreground">{t("dependents.policyNumber")}</p><p className="text-sm text-foreground">{selectedDependent.insurancePolicyNumber || "\u2014"}</p></div>
                    <div><p className="text-xs text-muted-foreground">{t("dependents.expiryDate")}</p><p className="text-sm text-foreground">{selectedDependent.insuranceExpiryDate ? new Date(selectedDependent.insuranceExpiryDate).toLocaleDateString() : "\u2014"}</p></div>
                  </div>
                </div>
                {(selectedDependent.emergencyContactName || selectedDependent.emergencyContactPhone) && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-3">{t("dependents.emergencyContact")}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div><p className="text-xs text-muted-foreground">{t("dependents.contactName")}</p><p className="text-sm text-foreground">{selectedDependent.emergencyContactName || "\u2014"}</p></div>
                      <div><p className="text-xs text-muted-foreground">{t("dependents.contactPhone")}</p><p className="text-sm text-foreground">{selectedDependent.emergencyContactPhone || "\u2014"}</p></div>
                    </div>
                  </div>
                )}
                {selectedDependent.medicalConditions.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-3">{t("dependents.medicalConditions")}</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedDependent.medicalConditions.map((c, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-1 bg-destructive/10 text-destructive rounded-md text-sm">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
                {selectedDependent.notes && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">{t("dependents.notes")}</h3>
                    <p className="text-sm text-muted-foreground">{selectedDependent.notes}</p>
                  </div>
                )}

                {/* Documents */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">{t("dependents.docs.title")}</h3>
                  {docsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Spinner size="sm" />
                    </div>
                  ) : documents.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">{t("dependents.docs.noDocuments")}</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {documents.map((doc) => (
                        <div key={doc.id} className="border border-border rounded-lg p-3 hover:border-primary/30 transition-colors">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 bg-primary/5 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-4.5 h-4.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate" title={doc.fileName}>{doc.fileName}</p>
                              <p className="text-xs text-muted-foreground">{t(`dependents.docs.type${documentTypeLabels[doc.documentType]}`)} &middot; {formatFileSize(doc.fileSize)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border">
                            <button onClick={() => handleViewDocument(doc.id)} className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded min-h-[44px] min-w-[44px] flex items-center justify-center" title={t("dependents.docs.view")}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DialogBody>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Insurance Modal */}
      {selectedDependent && (
        <UpdateInsuranceModal
          dependent={selectedDependent}
          open={insuranceModalOpen}
          onClose={() => setInsuranceModalOpen(false)}
          onUpdated={handleInsuranceUpdated}
        />
      )}

      {/* Bulk Update Insurance Modal */}
      <BulkUpdateInsuranceModal
        dependents={selectedDependents}
        open={bulkInsuranceModalOpen}
        onClose={() => setBulkInsuranceModalOpen(false)}
        onUpdated={handleBulkInsuranceUpdated}
      />
    </PageLayout>
  );
}
